/**
 * @fileoverview Flags declared in code, created in the dashboard.
 * @module lib/features/core/flagDeclare
 *
 * ```ts
 * Flowgrid.init({ webId, flags: { declare: ['new-checkout', { key: 'beta-banner', description: 'Top banner' }] } });
 * await getServerFlags({ webId, declare: ['new-checkout'] });
 * ```
 *
 * A declared key the website's bundle does not have yet is sent to core, which
 * creates it in the dashboard **off**. Declaring can never turn a flag on, and
 * never changes a flag that already exists — created in the dashboard or by an
 * earlier declaration. Keys already in the bundle cost nothing: the comparison
 * is local and no request is made.
 *
 * Keys are taken exactly as written. `flags.on()` reads the exact string, so a
 * key we "fixed" on the way in would create a flag the code never matches.
 *
 * A flag whose rules target people is left out of the public bundle, so it
 * looks missing here; core drops it against the full bundle, and the memo below
 * keeps that to one request an hour rather than one per page view.
 */

import type { FlagBundle } from './flagEval';

export type FlagDeclaration = string | { key: string; description?: string };

export interface NormalisedDeclaration {
  key: string;
  description?: string;
}

/** Same shape the dashboard accepts; anything else would be refused there. */
const FLAG_KEY = /^[a-z0-9][a-z0-9.-]{1,62}[a-z0-9]$/;

/** Matches core's per-request cap. */
export const MAX_DECLARATIONS = 25;

const MAX_DESCRIPTION_LENGTH = 280;

const RESEND_AFTER_MS = 60 * 60 * 1000;

const STORAGE_PREFIX = 'fg_flags_declared:';

/** Process memory on a server, mirrored to localStorage in a browser. */
const sentAt = new Map<string, number>();

export function normaliseDeclarations(input: unknown): NormalisedDeclaration[] {
  if (!Array.isArray(input)) return [];

  const byKey = new Map<string, NormalisedDeclaration>();

  for (const entry of input) {
    const key = typeof entry === 'string' ? entry : (entry as { key?: unknown })?.key;
    if (typeof key !== 'string' || !FLAG_KEY.test(key) || byKey.has(key)) continue;

    const description = typeof entry === 'object' ? (entry as { description?: unknown }).description : undefined;
    byKey.set(key, typeof description === 'string' && description.trim()
      ? { key, description: description.trim().slice(0, MAX_DESCRIPTION_LENGTH) }
      : { key });
  }

  return [...byKey.values()].slice(0, MAX_DECLARATIONS);
}

/** Declarations the bundle has no flag for. */
export function missingFrom(bundle: FlagBundle | null, declared: NormalisedDeclaration[]): NormalisedDeclaration[] {
  if (!bundle?.flags) return [];
  return declared.filter(({ key }) => !(key in bundle.flags));
}

function storageFor(webId: string): Record<string, number> {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_PREFIX + webId);
    return raw ? (JSON.parse(raw) as Record<string, number>) : {};
  } catch {
    return {};
  }
}

function recentlySent(webId: string, key: string, now: number): boolean {
  const at = sentAt.get(`${webId}:${key}`) ?? storageFor(webId)[key] ?? 0;
  return now - at < RESEND_AFTER_MS;
}

function rememberSent(webId: string, keys: string[], now: number): void {
  keys.forEach((key) => sentAt.set(`${webId}:${key}`, now));

  try {
    const stored = storageFor(webId);
    const fresh = Object.fromEntries(
      Object.entries(stored).filter(([, at]) => now - at < RESEND_AFTER_MS),
    );
    keys.forEach((key) => { fresh[key] = now; });
    globalThis.localStorage?.setItem(STORAGE_PREFIX + webId, JSON.stringify(fresh));
  } catch {
    /* storage blocked — process memory still holds it */
  }
}

/**
 * Fire-and-forget. Never throws and never delays a flag read — a declaration
 * that did not land is sent again by the first load an hour later that still
 * finds the key missing.
 */
export function sendDeclarations(endpoint: string, webId: string, declared: NormalisedDeclaration[]): void {
  if (typeof fetch === 'undefined') return;

  const now = Date.now();
  const flags = declared.filter(({ key }) => !recentlySent(webId, key, now));
  if (flags.length === 0) return;

  rememberSent(webId, flags.map(({ key }) => key), now);

  try {
    void fetch(`${endpoint}/api/v1/flags/declare`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ web_id: webId, flags }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* a blocked request is never an error path */
  }
}
